import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateRange } from "react-day-picker";
import {
  BarChart3,
  Loader2,
  RefreshCcw,
  Store as StoreIcon,
  Users as UsersIcon,
  Wallet,
} from "lucide-react";
import { useTranslation } from "@/shared/i18n";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { Option } from "@/shared/ui/multiple-selector";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { formatCurrency } from "@/shared/utils/format";
import {
  formatDateShort,
  toIsoEndOfDay,
  toIsoStartOfDay,
} from "@/shared/utils/date";
import {
  ReceivableCurrency,
  ReceivableStatus,
} from "@/features/receivables/utils/types";
import { ReceivableStatusBadge } from "@/features/receivables/components";
import { useFinancialOverviewSummary } from "../model/hooks";
import type { GetFinancialOverviewSummaryParams } from "../types";
import { CreatorRow } from "../components/CreatorRow";
import { FinancialFilters } from "../components/FinancialFilters";
import { HeaderWithTooltip } from "../components/HeaderWithTooltip";
import { KpiCard } from "../components/KpiCard";
import { EmptyRow, LoadingRow } from "../components/StateRows";

const ALL_STATUS = new Set<ReceivableStatus | "all">([
  "all",
  ReceivableStatus.WAITING,
  ReceivableStatus.CALCULATING,
  ReceivableStatus.CALCULATED,
  ReceivableStatus.RECEIVED,
  ReceivableStatus.CONCLUDED,
  ReceivableStatus.FAIL,
]);

const ALL_CURRENCY = new Set<ReceivableCurrency>([
  ReceivableCurrency.BRL,
  ReceivableCurrency.USD,
]);

function parseStatus(raw: string | null): ReceivableStatus | "all" {
  if (raw && ALL_STATUS.has(raw as ReceivableStatus | "all")) {
    return raw as ReceivableStatus | "all";
  }
  return "all";
}

function parseCurrency(raw: string | null): ReceivableCurrency {
  if (raw && ALL_CURRENCY.has(raw as ReceivableCurrency)) {
    return raw as ReceivableCurrency;
  }
  return ReceivableCurrency.BRL;
}

function parseDate(raw: string | null): Date | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d : undefined;
}

function defaultDateRange(): DateRange {
  const to = new Date();
  const from = new Date();
  from.setFullYear(from.getFullYear() - 1);
  return { from, to };
}

export const AdminFinancialOverviewPage = () => {
  const { t, i18n } = useTranslation("admin-financial-overview");
  const [searchParams, setSearchParams] = useSearchParams();

  // === Filtros (estado local) — inicializa a partir da URL com validação. ===
  const [selectedCreators, setSelectedCreators] = useState<Option[]>(() =>
    searchParams.getAll("creatorIds").map((id) => ({ value: id, label: id }))
  );
  const [creatorName, setCreatorName] = useState<string>(
    () => searchParams.get("creatorName") ?? ""
  );
  const debouncedCreatorName = useDebounce(creatorName, 400);
  const [statusFilter, setStatusFilter] = useState<ReceivableStatus | "all">(
    () => parseStatus(searchParams.get("status"))
  );
  const [currency, setCurrency] = useState<ReceivableCurrency>(() =>
    parseCurrency(searchParams.get("currency"))
  );
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const from = parseDate(searchParams.get("dateFrom"));
    const to = parseDate(searchParams.get("dateTo"));
    if (!from && !to) return defaultDateRange();
    return { from, to };
  });
  const [expandedCreatorId, setExpandedCreatorId] = useState<string | null>(
    null
  );

  // === Sync URL (chamado pelos handlers, não em useEffect — evita loop). ===
  const writeSearchParams = useCallback(
    (next: {
      creators?: Option[];
      creatorName?: string;
      status?: ReceivableStatus | "all";
      currency?: ReceivableCurrency;
      dateRange?: DateRange | undefined;
    }) => {
      const usp = new URLSearchParams();
      const creators = next.creators ?? selectedCreators;
      const name = next.creatorName ?? creatorName;
      const s = next.status ?? statusFilter;
      const c = next.currency ?? currency;
      const dr = next.dateRange === undefined ? dateRange : next.dateRange;

      for (const it of creators) usp.append("creatorIds", it.value);
      if (name.trim()) usp.set("creatorName", name.trim());
      if (s !== "all") usp.set("status", s);
      if (c) usp.set("currency", c);
      const from = toIsoStartOfDay(dr?.from);
      const to = toIsoEndOfDay(dr?.to);
      if (from) usp.set("dateFrom", from);
      if (to) usp.set("dateTo", to);

      setSearchParams(usp, { replace: true });
    },
    [
      selectedCreators,
      creatorName,
      statusFilter,
      currency,
      dateRange,
      setSearchParams,
    ]
  );

  // === Handlers ===
  const handleCreatorsChange = useCallback(
    (next: Option[]) => {
      setSelectedCreators(next);
      writeSearchParams({ creators: next });
    },
    [writeSearchParams]
  );

  const handleCreatorNameChange = useCallback(
    (next: string) => {
      setCreatorName(next);
      writeSearchParams({ creatorName: next });
    },
    [writeSearchParams]
  );

  const handleStatusChange = useCallback(
    (next: ReceivableStatus | "all") => {
      setStatusFilter(next);
      writeSearchParams({ status: next });
    },
    [writeSearchParams]
  );

  const handleCurrencyChange = useCallback(
    (next: ReceivableCurrency) => {
      setCurrency(next);
      writeSearchParams({ currency: next });
    },
    [writeSearchParams]
  );

  const handleDateRangeChange = useCallback(
    (next: DateRange | undefined) => {
      setDateRange(next);
      writeSearchParams({ dateRange: next });
    },
    [writeSearchParams]
  );

  // === Params para a query ===
  const params = useMemo<GetFinancialOverviewSummaryParams>(() => {
    const p: GetFinancialOverviewSummaryParams = { currency };
    if (selectedCreators.length) {
      p.creatorIds = selectedCreators.map((c) => c.value);
    }
    const trimmedName = debouncedCreatorName.trim();
    if (trimmedName.length >= 2) {
      p.creatorName = trimmedName;
    }
    if (statusFilter !== "all") {
      p.status = [statusFilter];
    }
    const dateFrom = toIsoStartOfDay(dateRange?.from);
    const dateTo = toIsoEndOfDay(dateRange?.to);
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo) p.dateTo = dateTo;
    return p;
  }, [
    currency,
    selectedCreators,
    debouncedCreatorName,
    statusFilter,
    dateRange,
  ]);

  const { summary, isLoading, isFetching, refetch } =
    useFinancialOverviewSummary(params);

  const totals = summary?.totals;
  const responseCurrency = summary?.currency ?? currency;

  const sortedByCreator = useMemo(() => {
    if (!summary?.byCreator) return [];
    return [...summary.byCreator].sort(
      (a, b) => b.totalShareAmount - a.totalShareAmount
    );
  }, [summary?.byCreator]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-muted-foreground">{t("subtitle")}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCcw className="mr-2 h-4 w-4" />
          )}
          {t("refresh")}
        </Button>
      </div>

      {/* Filtros */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>{t("filters")}</CardTitle>
          <CardDescription>{t("filtersHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <FinancialFilters
            selectedCreators={selectedCreators}
            onCreatorsChange={handleCreatorsChange}
            creatorName={creatorName}
            onCreatorNameChange={handleCreatorNameChange}
            status={statusFilter}
            onStatusChange={handleStatusChange}
            currency={currency}
            onCurrencyChange={handleCurrencyChange}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
          />
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title={t("kpiGrossTotal")}
          icon={<Wallet className="h-4 w-4 text-muted-foreground" />}
          value={
            totals
              ? formatCurrency(responseCurrency, totals.grossAmount)
              : "—"
          }
          isLoading={isLoading}
        />
        <KpiCard
          title={t("kpiShareTotal")}
          icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
          value={
            totals
              ? formatCurrency(responseCurrency, totals.shareAmount)
              : "—"
          }
          isLoading={isLoading}
        />
        <KpiCard
          title={t("kpiReceivables")}
          icon={<StoreIcon className="h-4 w-4 text-muted-foreground" />}
          value={totals ? String(totals.receivablesCount) : "—"}
          isLoading={isLoading}
        />
        <KpiCard
          title={t("kpiCreators")}
          icon={<UsersIcon className="h-4 w-4 text-muted-foreground" />}
          value={totals ? String(totals.creatorsCount) : "—"}
          isLoading={isLoading}
        />
      </div>

      {/* Período */}
      {summary?.period && (
        <p className="text-sm text-muted-foreground">
          {t("period")}: {formatDateShort(summary.period.from)} →{" "}
          {formatDateShort(summary.period.to)}
        </p>
      )}

      {/* Por status */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>{t("byStatus")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {isLoading ? (
            <LoadingRow />
          ) : !summary?.byStatus.length ? (
            <EmptyRow />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("colStatus")}</TableHead>
                  <TableHead className="text-right">{t("colCount")}</TableHead>
                  <TableHead className="text-right">{t("colGross")}</TableHead>
                  <TableHead className="text-right">{t("colShare")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.byStatus.map((row) => (
                  <TableRow key={row.status}>
                    <TableCell>
                      <ReceivableStatusBadge status={row.status} />
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.count}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(responseCurrency, row.grossAmount)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(responseCurrency, row.shareAmount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Por loja */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>{t("byStore")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {isLoading ? (
            <LoadingRow />
          ) : !summary?.byStore.length ? (
            <EmptyRow />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("colStore")}</TableHead>
                  <TableHead className="text-right">{t("colCount")}</TableHead>
                  <TableHead className="text-right">{t("colGross")}</TableHead>
                  <TableHead className="text-right">{t("colShare")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.byStore.map((row) => (
                  <TableRow key={row.store}>
                    <TableCell className="font-medium">{row.store}</TableCell>
                    <TableCell className="text-right font-mono">
                      {row.count}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(responseCurrency, row.grossAmount)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(responseCurrency, row.shareAmount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Por creator */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t("byCreator")}</CardTitle>
            <CardDescription>
              {sortedByCreator.length
                ? t("byCreatorHint", { count: sortedByCreator.length })
                : t("noData")}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {isLoading ? (
            <LoadingRow />
          ) : !sortedByCreator.length ? (
            <EmptyRow />
          ) : (
            <TooltipProvider delayDuration={200}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>{t("colCreator")}</TableHead>
                    <TableHead>
                      <HeaderWithTooltip
                        label={t("colCreatorStatus")}
                        hint={t("colCreatorStatusTooltip")}
                      />
                    </TableHead>
                    <TableHead className="text-right">
                      <HeaderWithTooltip
                        label={t("colCount")}
                        hint={t("colCountTooltip")}
                      />
                    </TableHead>
                    <TableHead className="text-right">
                      <HeaderWithTooltip
                        label={t("colLastPayment")}
                        hint={t("colLastPaymentTooltip")}
                      />
                    </TableHead>
                    <TableHead className="text-right">
                      <HeaderWithTooltip
                        label={t("colTotalGeneral")}
                        hint={t("colTotalGeneralTooltip")}
                      />
                    </TableHead>
                    <TableHead className="text-right">
                      <HeaderWithTooltip
                        label={t("colAvgPct")}
                        hint={t("colAvgPctTooltip")}
                      />
                    </TableHead>
                    <TableHead className="text-right">
                      {t("colPaid")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("colPending")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedByCreator.map((row) => {
                    const isExpanded = expandedCreatorId === row.creatorId;
                    return (
                      <CreatorRow
                        key={row.creatorId}
                        creator={row}
                        currency={responseCurrency}
                        isExpanded={isExpanded}
                        onToggle={() =>
                          setExpandedCreatorId(
                            isExpanded ? null : row.creatorId
                          )
                        }
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
